import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Table from 'src/components/VehicleTrackingDetails/Tables/Table';
import {
  getVehicleTrackingLineItems,
  createVehicleTrackingLineItemRejectionLog,
  getVehicleTrackingLineItemRejectionLogs,
} from 'src/redux/Actions/VehicleTracking';
import MuiAlert from '@mui/material/Alert';
import 'src/style/vehicletrackingqualitycheck.css';

function QualityCheckReviewTab() {
  const dispatch = useDispatch();
  const { vehicleTrackingNumber, workflowStageId } = useParams();

  // State to manage rejection quantities for each line item
  const [rejectionQuantities, setRejectionQuantities] = useState({});

  // State for toast notifications
  const [invalidFormSubmissionDetails, setInvalidFormSubmissionDetails] =
    useState({
      isInvalid: false,
      message: '',
    });

  // State for success notifications
  const [successNotification, setSuccessNotification] = useState({
    show: false,
    message: '',
  });

  const vehicleTrackingDataReducer = useSelector(
    (state) => state?.VehicleTrackingReducer,
  );
  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer.userData.tenant?.theme;
  const {
    vehicleTrackingLineItemList,
    vehicleTrackingLineItemRejectionLogs,
    vehicleTrackingDetails,
  } = vehicleTrackingDataReducer;

  // Helper function to get the actual rejected quantity for a specific line item
  const getActualRejectedQuantity = (lineItemId) => {
    const rejectionLog = vehicleTrackingLineItemRejectionLogs?.find(
      (log) => log.vehicle_tracking_line_item_id === lineItemId,
    );
    return rejectionLog ? parseFloat(rejectionLog.quantity || 0) : 0;
  };

  // Check if input and reject buttons should be disabled
  const isInteractionDisabled =
    vehicleTrackingDetails?.requestedStage?.completed ||
    !vehicleTrackingDetails?.requestedStage?.entry_log_exist;

  useEffect(() => {
    if (vehicleTrackingLineItemList.length === 0) {
      dispatch(
        getVehicleTrackingLineItems(organisationId, vehicleTrackingNumber),
      );
    }

    // Prefill rejection logs on component start
    dispatch(
      getVehicleTrackingLineItemRejectionLogs(
        vehicleTrackingNumber,
        organisationId,
      ),
    );
  }, [dispatch, vehicleTrackingNumber, organisationId, workflowStageId]);

  // Initialize rejection quantities state when line items load
  useEffect(() => {
    if (vehicleTrackingLineItemList?.length > 0) {
      const initialQuantities = {};
      vehicleTrackingLineItemList.forEach((item) => {
        // Prefill with actual rejected quantity from rejection logs
        const actualRejected = getActualRejectedQuantity(item.id);
        initialQuantities[item.id] =
          actualRejected > 0 ? actualRejected.toString() : '';
      });
      setRejectionQuantities(initialQuantities);
    }
  }, [vehicleTrackingLineItemList, vehicleTrackingLineItemRejectionLogs]);

  // Update rejection quantities when rejection logs change (for real-time updates)
  useEffect(() => {
    if (
      vehicleTrackingLineItemList?.length > 0 &&
      vehicleTrackingLineItemRejectionLogs?.length >= 0
    ) {
      setRejectionQuantities((prev) => {
        const updatedQuantities = { ...prev };
        vehicleTrackingLineItemList.forEach((item) => {
          const actualRejected = getActualRejectedQuantity(item.id);
          // Only update if the current value is empty or different from actual rejected
          if (
            !updatedQuantities[item.id] ||
            updatedQuantities[item.id] === '' ||
            parseFloat(updatedQuantities[item.id]) !== actualRejected
          ) {
            updatedQuantities[item.id] =
              actualRejected > 0 ? actualRejected.toString() : '';
          }
        });
        return updatedQuantities;
      });
    }
  }, [vehicleTrackingLineItemRejectionLogs, vehicleTrackingLineItemList]);

  // Auto-dismiss error notifications after 5 seconds
  useEffect(() => {
    if (invalidFormSubmissionDetails.isInvalid) {
      const timer = setTimeout(() => {
        setInvalidFormSubmissionDetails({ isInvalid: false, message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [invalidFormSubmissionDetails.isInvalid]);

  // Auto-dismiss success notifications after 3 seconds
  useEffect(() => {
    if (successNotification.show) {
      const timer = setTimeout(() => {
        setSuccessNotification({ show: false, message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [successNotification.show]);

  const headers = [
    {
      label: 'PO Number',
      value: 'purchase_order_number',
    },
    { label: 'Invoice Number', value: 'invoice_number' },
    {
      label: 'Material Code',
      value: 'material_code',
    },
    {
      label: 'Material Description',
      value: 'material_description',
    },
    {
      label: 'Receiving Quantity',
      value: 'receiving_quantity',
    },
    {
      label: 'Pack Type',
      value: 'pack_type',
    },
    {
      label: 'Reject Quantity',
      value: 'reject_quantity',
    },
    {
      label: 'Actions',
      value: 'actions',
    },
  ];

  // Handle rejection quantity input change
  const handleRejectionQuantityChange = (lineItemId, value) => {
    if (isInteractionDisabled) return;

    setRejectionQuantities((prev) => ({
      ...prev,
      [lineItemId]: value,
    }));
  };

  // Handle reject button click
  const handleRejectLineItem = (lineItem) => {
    if (isInteractionDisabled) return;

    const rejectionQty = rejectionQuantities[lineItem.id];

    if (!rejectionQty || rejectionQty < 0) {
      setInvalidFormSubmissionDetails({
        isInvalid: true,
        message:
          'Please enter a valid rejection quantity greater than or equal to 0.',
      });
      return;
    }

    if (parseFloat(rejectionQty) > parseFloat(lineItem.receiving_quantity)) {
      setInvalidFormSubmissionDetails({
        isInvalid: true,
        message: `Rejection quantity (${rejectionQty}) cannot exceed receiving quantity (${lineItem.receiving_quantity}).`,
      });
      return;
    }

    // Call Redux action to create vehicle tracking line item rejection log
    dispatch(
      createVehicleTrackingLineItemRejectionLog(
        organisationId,
        lineItem.id,
        rejectionQty,
      ),
    )
      .then(() => {
        // Get updated rejection logs
        dispatch(
          getVehicleTrackingLineItemRejectionLogs(
            vehicleTrackingNumber,
            organisationId,
          ),
        );

        // Show success notification
        setSuccessNotification({
          show: true,
          message: `Successfully rejected ${rejectionQty} ${lineItem?.unit_of_measurement} for ${lineItem?.material_code}.`,
        });
      })
      .catch(() => {
        // Show error notification
        setInvalidFormSubmissionDetails({
          isInvalid: true,
          message: 'Failed to create rejection log. Please try again.',
        });
      });
  };

  const renderTableContent = () => (
    <tbody>
      {vehicleTrackingLineItemList?.length > 0 ? (
        vehicleTrackingLineItemList.map((lineItem) => (
          <tr key={lineItem.id}>
            {headers.map((header) => {
              let content;

              if (header.value === 'reject_quantity') {
                // Render input field for rejection quantity
                content = (
                  <div className="div-flex-row div-align-center cg-10">
                    <input
                      type="number"
                      min="0"
                      max={lineItem.receiving_quantity}
                      step="1"
                      value={rejectionQuantities[lineItem.id] || ''}
                      onChange={(e) =>
                        handleRejectionQuantityChange(
                          lineItem.id,
                          e.target.value,
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      className="quality-check-review-rejection-input"
                      placeholder="0"
                      title={
                        isInteractionDisabled
                          ? 'Input disabled - stage completed or no entry log'
                          : 'Prefilled with existing rejection quantity'
                      }
                      disabled={isInteractionDisabled}
                    />
                    <span className="text-sm text-grey">
                      {lineItem?.unit_of_measurement}
                    </span>
                  </div>
                );
              } else if (header.value === 'actions') {
                // Render reject button
                content = (
                  <div
                    onClick={
                      isInteractionDisabled
                        ? undefined
                        : () => handleRejectLineItem(lineItem)
                    }
                    className={`${
                      isInteractionDisabled ||
                      !rejectionQuantities[lineItem.id] ||
                      rejectionQuantities[lineItem.id] < 0
                        ? 'pill pill--disable-button'
                        : 'pill pill--reject-button'
                    }`}
                    disabled={
                      isInteractionDisabled ||
                      !rejectionQuantities[lineItem.id] ||
                      rejectionQuantities[lineItem.id] < 0
                    }
                    title={
                      isInteractionDisabled
                        ? 'Reject button disabled - stage completed or no entry log'
                        : 'Click to reject this line item'
                    }
                  >
                    <span className="text-sm text-semi-bold">Reject</span>
                  </div>
                );
              } else if (header.value === 'receiving_quantity') {
                content = `${lineItem[header.value]} ${lineItem?.unit_of_measurement}`;
              } else {
                content = lineItem[header.value];
              }

              return (
                <td
                  className="table-data"
                  key={`${lineItem.id}-${header.value}`}
                >
                  <div className="div-flex-row">
                    {typeof content === 'string' ? (
                      <span className="text-sm">{content}</span>
                    ) : (
                      content
                    )}
                  </div>
                </td>
              );
            })}
          </tr>
        ))
      ) : (
        <tr>
          <td
            colSpan={headers?.length}
            className="no-data-found-message border-bottom"
          >
            <span className="text-sm">No Vehicle Data Found</span>
          </td>
        </tr>
      )}
    </tbody>
  );

  return vehicleTrackingLineItemList?.length > 0 ? (
    <>
      {invalidFormSubmissionDetails.isInvalid && (
        <div className="margin-top-10 mb-3 left-padding right-padding">
          <MuiAlert
            onClose={() =>
              setInvalidFormSubmissionDetails({ isInvalid: false, message: '' })
            }
            severity="error"
            elevation={6}
            variant="filled"
          >
            {invalidFormSubmissionDetails.message}
          </MuiAlert>
        </div>
      )}

      {successNotification.show && (
        <div className="margin-top-10 mb-3 left-padding right-padding">
          <MuiAlert
            onClose={() => setSuccessNotification({ show: false, message: '' })}
            severity="success"
            elevation={6}
            variant="filled"
          >
            {successNotification.message}
          </MuiAlert>
        </div>
      )}

      <div className="div-flex-row div-space-between section-title div-align-center">
        <div className="text-md text-semi-bold">
          Line Items for Quality Review
        </div>
        <span className="text-md text-semi-bold">
          Total Items: {vehicleTrackingLineItemList?.length || 0}
        </span>
      </div>
      <div className="div-flex-column rg-10 mb-3">
        <div className="div-flex-row div-align-center cg-10">
          <div className="quality-check-review-info-box">
            <span className="text-sm text-info">
              <span className="text-bold">Instructions: </span>
              <span>
                Enter the quantity you want to reject for each line item and
                click the &quot;Reject&quot; button.
              </span>
            </span>
          </div>
        </div>
        <div className="div-flex-column rg-10">
          <ul className="text-xs text-grey">
            <li>Rejection quantity cannot exceed the receiving quantity</li>
            <li>
              Leave rejection quantity empty or 0 if no rejection is needed
            </li>
          </ul>
        </div>
      </div>
      <div
        className={`table-fixed-container theme-horizontal-scrollable-container-${themeColor}`}
      >
        <Table
          headers={headers}
          minCellWidth={150}
          tableContent={renderTableContent()}
        />
      </div>
    </>
  ) : (
    <div className="vehicle-data-add-button-container div-flex-center div-align-center rg-10">
      <div className="div-flex-column div-align-center">
        <span className="text-lg text-semi-bold">
          No Vehicle Content Found for Quality Review
        </span>
        <span className="text-grey">
          Vehicle contents need to be added before quality review can be
          performed.
        </span>
      </div>
    </div>
  );
}

export default QualityCheckReviewTab;
